import React, { type MouseEvent, useEffect, useState } from 'react';

import { track, useMatchMedia } from '@betterment/js-runtime';
import { Box, Card, Dialog, Heading, Icon, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Button, useTheme } from '@betterment/style-closet-react';

import { trackClick } from '../../features/track';
import { type ContentCardProps } from '../data';

type DisclosureModalProps = {
  fullDisclosure: string;
  handleModalClose: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
};

const DisclosureModal: React.FC<DisclosureModalProps> = ({ fullDisclosure, handleModalClose, isOpen }) => {
  return (
    <Dialog onOpenChange={() => handleModalClose(false)} open={isOpen} overlay="default">
      <Dialog.Content css={{ p: 'space.300' }} size="large">
        <Dialog.Title asChild={true}>
          <Heading as="h2" variant="title300" css={{ mb: 'space.100' }}>
            Disclosure
          </Heading>
        </Dialog.Title>

        <Dialog.Description asChild={true}>
          <Text dangerouslySetInnerHTML={{ __html: fullDisclosure }} />
        </Dialog.Description>

        <Box css={{ mt: 'space.200' }}>
          <Button spacing="compact" onClick={() => handleModalClose(false)} aria-label="Dismiss card">
            Close
          </Button>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

const RewardsContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  linkText,
  imageUrl,
  dismissible,
  url,
  handleDismissal,
  handleCardClick,
  trackingModule: eventModule,
  trackingFlow: eventFlow,
  extras: { disclosureUrl, shortDisclosure, fullDisclosure, name: eventName, disclosureLink },
}) => {
  const { theme } = useTheme();
  const isAtLeastSmall = useMatchMedia('atLeastSmall');
  const isAtLeastMedium = useMatchMedia('atLeastMedium');
  const [shouldShowDisclosureModal, setShouldShowDisclosureModal] = useState(false);

  useEffect(() => {
    if (eventName) {
      track('ElementViewed', {
        name: eventName,
        module: 'RewardsCenterContentCard',
        flow: 'Rewards Center',
      });
    }
  }, [eventName]);

  return (
    <Card>
      <Card.Content>
        <Box
          css={{
            position: 'relative',
            display: isAtLeastMedium ? 'flex' : 'block',
          }}
        >
          {imageUrl && (
            <Box
              style={{
                backgroundImage: `url(${imageUrl})`,
              }}
              css={{
                minHeight: isAtLeastMedium ? '20rem' : 'auto',
                maxWidth: isAtLeastMedium ? '25%' : 'none',
                width: '100%',
                height: isAtLeastMedium ? 'auto' : '20rem',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
          )}

          <Box
            css={{
              minHeight: isAtLeastMedium ? '20rem' : 'auto',
              maxWidth: imageUrl && isAtLeastMedium ? '75%' : 'none',
              width: '100%',
              p: 'space.300',
            }}
          >
            <Heading as="h2" variant="title300" css={{ mb: 'space.200' }}>
              {title}
            </Heading>
            <Text dangerouslySetInnerHTML={{ __html: description }} />
            <Separator css={{ mt: 'space.300', mb: 'space.100' }} />

            {url && linkText && (
              <Box css={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                  style={{ fontWeight: theme.fontWeights.medium }}
                  onClick={(event: MouseEvent) => {
                    trackClick({
                      name: `${eventName}_${linkText}`,
                      module: eventModule,
                      flow: eventFlow,
                    });

                    handleCardClick?.(event);
                  }}
                  rel="noopener noreferrer"
                  width={[1, 1, 'auto', 'auto']}
                  py={[4, 4, 4, 3]}
                  size="small"
                  fontSize={1}
                >
                  {linkText}
                </Button>
              </Box>
            )}
          </Box>

          {dismissible && !isAtLeastSmall && (
            <Box css={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
              <Button
                aria-label="Dismiss card"
                onClick={(event: MouseEvent) => {
                  trackClick({
                    name: `${eventName}_dismiss`,
                    module: eventModule,
                    flow: eventFlow,
                  });

                  handleDismissal?.(event);
                }}
                variant="tertiary"
              >
                <Icon color="icon" name="X" size="small" aria-hidden="true" />
              </Button>
            </Box>
          )}
        </Box>

        {!disclosureUrl && shortDisclosure && (
          <Box>
            <Separator />
            <Box css={{ p: 'space.200' }}>
              <Text
                css={{ fontSize: 'fontSize.100', display: 'inline-block' }}
                dangerouslySetInnerHTML={{ __html: shortDisclosure }}
              />
              {fullDisclosure && (
                <>
                  {shouldShowDisclosureModal && (
                    <DisclosureModal
                      fullDisclosure={fullDisclosure}
                      handleModalClose={setShouldShowDisclosureModal}
                      isOpen={shouldShowDisclosureModal}
                    />
                  )}

                  <Box as="span">
                    <Button
                      variant="tertiary"
                      style={{
                        fontSize: theme.fontSizes[1],
                        lineHeight: theme.lineHeights[0],
                        display: 'inline',
                        marginLeft: theme.space[1],
                      }}
                      onClick={() => {
                        trackClick({
                          name: `${eventName}_full_disclosure`,
                          module: eventModule,
                          flow: eventFlow,
                        });

                        setShouldShowDisclosureModal(true);
                      }}
                    >
                      {disclosureLink}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}

        {disclosureUrl && disclosureLink && (
          <Box>
            <Separator />
            <Box css={{ p: 'space.200' }}>
              <SambaAutoLink
                to={disclosureUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track('ElementClicked', {
                    name: `${eventName}_disclosure_url`,
                    module: eventModule,
                    flow: eventFlow,
                  });
                }}
              >
                {disclosureLink}
              </SambaAutoLink>
            </Box>
          </Box>
        )}
      </Card.Content>
    </Card>
  );
};

export default RewardsContentCard;
